import { Box } from '@chakra-ui/react';
import {
  type CoachNoteSections,
  type Coverage,
  type CoverageStatus,
  type GroundedIn,
  type Priority,
  type ThreadKind,
  type TimeSegment,
  type SeekMoment,
  parseTimestamp,
} from './api';
import './coach.scss';

export type { SeekMoment };

export interface CoachMeta {
  candidateName: string;
  round: string;
  role: string;
  minutes: number;
}

const SEG_PALETTE = [
  'var(--kula-colors-secondary-300)',
  'var(--kula-colors-primary-500)',
  'var(--kula-colors-primary-600)',
  'var(--kula-colors-status-3)',
  'var(--kula-colors-status-4)',
  'var(--kula-colors-secondary-500)',
  'var(--kula-colors-secondary-400)',
  'var(--kula-colors-pastel-3-dark)',
  'var(--kula-colors-pastel-4-dark)',
];

const PRI_LABEL: Record<Priority, string> = {
  must_cover: 'Critical',
  core: 'Important',
  optional: 'Good to cover',
};
const SRC_LABEL: Record<GroundedIn, string> = {
  scorecard_question: 'Scorecard',
  job_description: 'Job description',
  candidate_background: 'Candidate background',
  prior_round_gap: 'Prior-round gap',
};
// The hover reads as a citation: this caption attributes why the area is here, then the verbatim source.
const SRC_CAPTION: Record<GroundedIn, string> = {
  scorecard_question: "Asked for in this round's scorecard",
  job_description: 'Required by the job description',
  candidate_background: "From the candidate's own background",
  prior_round_gap: 'Left unverified by an earlier round',
};
const COV_SPINE: Record<Priority, string> = {
  must_cover: 'var(--kula-colors-primary-500)',
  core: 'var(--kula-colors-status-4)',
  optional: 'var(--kula-colors-secondary-300)',
};
const THREAD_LABEL: Record<ThreadKind, string> = {
  failure: 'Failure',
  conflict: 'Conflict',
  tradeoff: 'Trade-off',
  decision: 'Decision',
};

const Check = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    {...p}
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const CovCheck = () => (
  <svg
    viewBox="0 0 24 24"
    width="11"
    height="11"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const CovDash = () => (
  <svg
    viewBox="0 0 24 24"
    width="11"
    height="11"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
  >
    <path d="M5 12h14" />
  </svg>
);
const CovX = () => (
  <svg
    viewBox="0 0 24 24"
    width="11"
    height="11"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const COV_CHIP: Record<
  CoverageStatus,
  { label: string; icon: React.ReactNode }
> = {
  covered: { label: 'Covered', icon: <CovCheck /> },
  partial: { label: 'Partial', icon: <CovDash /> },
  skipped: { label: 'Skipped', icon: <CovX /> },
};

// Transcript-moment pill. When the recording is available and the timestamp parses, it's a button that opens
// the moment player. Recall finalizes the video a few minutes after the call, so until it lands there's nothing
// to open — keep the timestamp readable but explain WHY it isn't clickable yet rather than looking broken.
const Tstamp = ({ ts, onSeek }: { ts?: string; onSeek?: SeekMoment }) => {
  if (!ts) return null;
  const seconds = parseTimestamp(ts);
  if (seconds === null) return <span className="tstamp">{ts}</span>; // unparseable — nothing to jump to, ever
  if (!onSeek) {
    return (
      <span
        className="tstamp"
        title="Recording is still processing — you’ll be able to jump to this moment shortly"
      >
        {ts}
      </span>
    );
  }
  return (
    <button
      type="button"
      className="tstamp tstamp-clickable"
      onClick={(e) =>
        onSeek(seconds, ts, e.currentTarget.getBoundingClientRect())
      }
    >
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z" />
      </svg>
      {ts}
    </button>
  );
};

const Section = ({
  num,
  title,
  sub,
  delay,
  children,
}: {
  num: number;
  title: string;
  sub?: string;
  delay: string;
  children: React.ReactNode;
}) => (
  <Box as="section" className={`coach-reveal ${delay}`}>
    <div className="sec-head">
      <div className="sec-num">{num}</div>
      <div className="sec-title">{title}</div>
      {sub && <div className="sec-sub">{sub}</div>}
    </div>
    {children}
  </Box>
);

// Planned vs. actual time bars, derived from the timestamped transcript (matches the prototype's bars()).
function TimeBars({ segments }: { segments: TimeSegment[] }) {
  const names: string[] = [];
  segments.forEach((s) => {
    if (!names.includes(s.segment)) names.push(s.segment);
  });
  const color = (n: string) =>
    SEG_PALETTE[names.indexOf(n) % SEG_PALETTE.length];
  const plannedTotal = segments.reduce((a, s) => a + (s.planned_mins || 0), 0);
  const actualTotal = segments.reduce((a, s) => a + (s.actual_mins || 0), 0);
  const gap = plannedTotal - actualTotal;

  return (
    <>
      <div className="bar-row">
        <div className="bar-tag">Planned</div>
        <div className="bar">
          {segments
            .filter((s) => s.planned_mins > 0)
            .map((s, i) => (
              <div
                key={i}
                className="seg"
                style={{ flex: s.planned_mins, background: color(s.segment) }}
              >
                {s.planned_mins}′
                <div className="tip">
                  <b>{s.segment}</b>
                  <br />
                  planned {s.planned_mins} min
                </div>
              </div>
            ))}
        </div>
      </div>
      <div className="bar-row actual">
        <div className="bar-tag">Actual</div>
        <div className="bar">
          {segments
            .filter((s) => s.actual_mins > 0)
            .map((s, i) => (
              <div
                key={i}
                className="seg"
                style={{ flex: s.actual_mins, background: color(s.segment) }}
              >
                {s.actual_mins}′
                <div className="tip">
                  <b>
                    {s.segment}
                    {s.planned_mins === 0 ? ' (unplanned)' : ''}
                  </b>
                  <br />
                  planned {s.planned_mins} → actual {s.actual_mins} min
                  {s.note ? ` · ${s.note}` : ''}
                </div>
              </div>
            ))}
          {gap > 0 && (
            <div className="seg ghost" style={{ flex: gap }}>
              −{gap}′
              <div className="tip">
                <b>Unused · ended early</b>
                <br />
                {gap} min of the planned round never used
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="legend">
        {names.map((n, i) => (
          <div key={i} className="it">
            <div className="sw" style={{ background: color(n) }} />
            {n}
          </div>
        ))}
      </div>
    </>
  );
}

function CoverageRow({
  c,
  onAsk,
}: {
  c: Coverage;
  onAsk?: (area: string) => void;
}) {
  const chip = COV_CHIP[c.status];
  // A merged scorecard area cites several questions ('; '-joined) — split them so the hover lists
  // each one and the caption reflects how many were folded in.
  const srcParts =
    c.grounded_in === 'scorecard_question'
      ? c.source_ref
          .split(/;\s+/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [c.source_ref];
  const srcMulti = srcParts.length > 1;
  return (
    <div className="cov-item" onClick={() => onAsk?.(c.area)}>
      <div className="cov-top">
        <div
          className="pri"
          style={{
            background:
              COV_SPINE[c.priority] || 'var(--kula-colors-secondary-300)',
          }}
        />
        <div className="nm">
          <div className="a">{c.area}</div>
          <div className="b">
            <span
              className="cov-pri"
              style={{ color: COV_SPINE[c.priority] || undefined }}
            >
              {PRI_LABEL[c.priority] || c.priority}
            </span>
            <span className="src">
              <span className="src-chip">
                {SRC_LABEL[c.grounded_in] || c.grounded_in}
              </span>
              {c.source_ref && (
                <span className="src-tip">
                  <span className="src-tip-caption">
                    {srcMulti
                      ? `Covers ${srcParts.length} scorecard questions for this round`
                      : SRC_CAPTION[c.grounded_in] || ''}
                  </span>
                  {srcMulti ? (
                    <ul className="src-tip-list">
                      {srcParts.map((q, j) => (
                        <li key={j}>{q}</li>
                      ))}
                    </ul>
                  ) : (
                    <span className="src-tip-quote">{srcParts[0]}</span>
                  )}
                </span>
              )}
            </span>
          </div>
        </div>
        {chip && (
          <span className={`chip ${c.status}`}>
            {chip.icon}
            {chip.label}
          </span>
        )}
      </div>
      {(c.validated || c.gap) && (
        <div className="cov-lines">
          {c.validated && (
            <div className="cov-line got">
              <span className="cl-ic">
                <CovCheck />
              </span>
              <span>
                <span className="cl-lab">Captured</span>
                {c.validated}
              </span>
            </div>
          )}
          {c.gap && (
            <div className="cov-line open">
              <span className="cl-ic">
                <svg
                  viewBox="0 0 24 24"
                  width="11"
                  height="11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <path d="M12 8v5M12 16.5h.01" />
                </svg>
              </span>
              <span>
                <span className="cl-lab">Still open</span>
                {c.gap}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CoachNote({
  note,
  meta,
  onAskCoverage,
  onSeekMoment,
}: {
  note: CoachNoteSections;
  meta: CoachMeta;
  onAskCoverage?: (area: string) => void;
  onSeekMoment?: SeekMoment;
}) {
  const ta = note.time_allocation || { drift_note: '', segments: [] };
  const segs = ta.segments || [];
  const actualTotal = segs.reduce((a, s) => a + (s.actual_mins || 0), 0);
  let durline = `${meta.minutes} min planned`;
  if (segs.length) durline += ` · ended ~${actualTotal} min`;

  // Only the middle sections are numbered, sequentially among those actually present.
  const presentSections = [
    segs.length > 0 && 'time',
    note.what_went_well?.length > 0 && 'well',
    note.improvement_areas?.length > 0 && 'imp',
    note.unprobed_claims?.length > 0 && 'claims',
    note.dropped_threads?.length > 0 && 'threads',
    note.coverage?.length > 0 && 'coverage',
  ].filter(Boolean) as string[];
  const numOf = (key: string) => presentSections.indexOf(key) + 1;

  return (
    <Box
      className="coach-brief"
      maxW="720px"
      mx="auto"
      px="44px"
      py="40px"
      pb="120px"
    >
      {/* header */}
      <Box className="coach-reveal">
        <Box as="h1" className="title">
          How your interview went
        </Box>
        <div className="subtitle">
          <span>{meta.candidateName}</span>
          <span className="dot" />
          <span>{meta.role}</span>
          <span className="dot" />
          <span>{durline}</span>
          {meta.round && (
            <>
              <span className="dot" />
              <span>{meta.round}</span>
            </>
          )}
        </div>
      </Box>

      {/* summary */}
      <Box className="summary coach-reveal coach-d1">
        <div className="label">
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4l2.5 2.5" />
          </svg>
          The read
        </div>
        <div className="lede">{note.summary}</div>
      </Box>

      {/* 1. plan vs the room */}
      {segs.length > 0 && (
        <Section
          num={numOf('time')}
          title="Your plan vs. the room"
          sub={`from your prep · ${meta.minutes} min on the clock`}
          delay="coach-d2"
        >
          <div className="glance">
            <TimeBars segments={segs} />
            {ta.drift_note && <div className="drift-note">{ta.drift_note}</div>}
          </div>
        </Section>
      )}

      {/* 2. what went well */}
      {note.what_went_well?.length > 0 && (
        <Section num={numOf('well')} title="What went well" delay="coach-d3">
          <div className="well">
            {note.what_went_well.map((w, i) => (
              <div key={i} className="well-card">
                <div className="well-ic">
                  <Check />
                </div>
                <div>
                  <h4>{w.title}</h4>
                  <p>{w.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 3. sharpen for next time */}
      {note.improvement_areas?.length > 0 && (
        <Section
          num={numOf('imp')}
          title="Sharpen for next time"
          sub="each one is a habit, not a verdict"
          delay="coach-d4"
        >
          <div className="imp">
            {note.improvement_areas.map((a, i) => {
              const big = a.severity === 'big_one';
              return (
                <div key={i} className={`imp-card${big ? ' hi' : ''}`}>
                  <div className="imp-main">
                    <div className="imp-idx">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="imp-body">
                      <div className="imp-head">
                        <h4>{a.title}</h4>
                        {big && (
                          <span className="badge big-one">The big one</span>
                        )}
                        <Tstamp ts={a.moment_timestamp} onSeek={onSeekMoment} />
                      </div>
                      <p>{a.narrative}</p>
                      <div className="why-next">
                        {a.why_it_matters && (
                          <div className="why">
                            <span className="lab">Why it matters</span>
                            {a.why_it_matters}
                          </div>
                        )}
                        <div className="next">
                          <span className="lab">Next time</span>
                          {a.next_time}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* 4. claims worth probing */}
      {note.unprobed_claims?.length > 0 && (
        <Section
          num={numOf('claims')}
          title="Claims worth probing next time"
          sub={`${note.unprobed_claims.length} ${note.unprobed_claims.length === 1 ? 'claim' : 'claims'} passed without an evidence question`}
          delay="coach-d5"
        >
          <div className="claims">
            {note.unprobed_claims.map((c, i) => (
              <div key={i} className="claim-card">
                <div className="claim-head">
                  <Tstamp ts={c.timestamp} onSeek={onSeekMoment} />
                </div>
                <div className="claim-quote">{c.quote}</div>
                <div className="claim-probe">
                  <span className="lab">Ask</span>
                  {c.suggested_probe}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 5. threads worth catching */}
      {note.dropped_threads?.length > 0 && (
        <Section
          num={numOf('threads')}
          title="Threads worth catching"
          sub="rich signal the conversation moved past"
          delay="coach-d6"
        >
          <div className="threads">
            {note.dropped_threads.map((t, i) => (
              <div key={i} className="thread-card">
                <div className="thread-head">
                  <span className="badge thread-b">
                    {THREAD_LABEL[t.kind] || t.kind}
                  </span>
                  <h4>{t.title}</h4>
                  <div className="ts-wrap">
                    <Tstamp ts={t.timestamp} onSeek={onSeekMoment} />
                  </div>
                </div>
                <div className="thread-quote">“{t.quote}”</div>
                <div className="missed">
                  <span className="lab">What was there</span>
                  {t.what_was_there}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 6. coverage vs prep */}
      {note.coverage?.length > 0 && (
        <Section
          num={numOf('coverage')}
          title="Coverage vs. your prep"
          delay="coach-d7"
        >
          <div className="cov">
            {note.coverage.map((c, i) => (
              <CoverageRow key={i} c={c} onAsk={onAskCoverage} />
            ))}
            <div className="cov-foot">
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              Click any area to unpack it with your coach — or highlight any
              text to ask about it.
            </div>
          </div>
        </Section>
      )}

      {/* one habit */}
      <Box className="habit coach-reveal coach-d8">
        <div className="label">
          <svg
            viewBox="0 0 24 24"
            width="13"
            height="13"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 3l1.9 4.6L18.5 9.5 14 11.4 12 16l-2-4.6L5.5 9.5 10 7.6z" />
          </svg>
          One habit for your next interview
        </div>
        <h3>{note.one_habit?.title}</h3>
        <p>{note.one_habit?.body}</p>
      </Box>

      <Box className="foot-note coach-reveal coach-d8">
        This note is about interview craft only — it makes no recommendation
        about the candidate, and it’s never part of the hiring decision. Only
        you can see it.
      </Box>
    </Box>
  );
}

export default CoachNote;
